// /grant-2026 — official Grant 2026 attribution page. Always dark, SQ/EN.
import { Fragment, useState } from "react"
import { Link } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import { ArrowLeft } from "lucide-react"
import { SEOHead } from "@/components/seo-head"
import { Reveal } from "@/components/shared/reveal"
import {
  GRANT_INK,
  GRANT_LOGOS,
  GRANT_TEXT,
  statementText,
  type GrantLang,
} from "@/components/shared/grant-attribution"

type Fact = { label: string; value: string; detail: string; href?: string }

const PAGE_TEXT: Record<GrantLang, {
  title: string
  eyebrow: string
  partnersHeading: string
  facts: Fact[]
  languageLabel: string
  backHome: string
  rights: string
  privacy: string
  terms: string
  support: string
}> = {
  sq: {
    title: "Grant 2026 · THE NOT FIT",
    eyebrow: "Grant 2026 · Republika e Shqipërisë",
    partnersHeading: "Partnerët institucionalë",
    facts: [
      { label: "Financuar nga", value: "Qeveria Shqiptare", detail: "Ministria e Ekonomisë dhe Inovacionit" },
      { label: "Skema", value: "Grant 2026", detail: "Mbështetje financiare" },
      { label: "Zbatuar nga", value: "Agjencia Innovation4Albania", detail: "Zbatues i projektit" },
      { label: "Platforma", value: "The Not Fit", detail: "Platformë fitness me AI", href: "/" },
    ],
    languageLabel: "Gjuha",
    backHome: "Kthehu në faqen kryesore",
    rights: "Të gjitha të drejtat e rezervuara.",
    privacy: "Privatësia",
    terms: "Kushtet",
    support: "Mbështetja",
  },
  en: {
    title: "Grant 2026 · THE NOT FIT",
    eyebrow: "Grant 2026 · Republic of Albania",
    partnersHeading: "Institutional partners",
    facts: [
      { label: "Financed by", value: "Albanian Government", detail: "Ministry of Economy and Innovation" },
      { label: "Scheme", value: "Grant 2026", detail: "Financial support" },
      { label: "Implemented by", value: "Innovation4Albania Agency", detail: "Project implementer" },
      { label: "Platform", value: "The Not Fit", detail: "AI-powered fitness platform", href: "/" },
    ],
    languageLabel: "Language",
    backHome: "Back to home",
    rights: "All rights reserved.",
    privacy: "Privacy",
    terms: "Terms",
    support: "Support",
  },
}

const LANGS: GrantLang[] = ["sq", "en"]

function initialLang(): GrantLang {
  if (typeof window === "undefined") return "sq"
  return localStorage.getItem("language") === "en" ? "en" : "sq"
}

export default function Grant2026Page() {
  const [lang, setLangState] = useState<GrantLang>(initialLang)
  const t = PAGE_TEXT[lang]
  const grant = GRANT_TEXT[lang]

  const setLang = (next: GrantLang) => {
    setLangState(next)
    try { localStorage.setItem("language", next) } catch { /* private mode: preference just isn't remembered */ }
  }

  return (
    <div lang={lang} className="relative min-h-screen overflow-hidden bg-[#0A0A0F] text-[#F5F3EE] antialiased">
      <SEOHead title={t.title} description={statementText(lang)} themeColor={GRANT_INK} />
      <Helmet>
        <style>{`body { background: ${GRANT_INK}; }`}</style>
      </Helmet>

      {/* Ambient: one breathing red glow, a faint grid fading downward, and the three brand shapes adrift. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-[-22rem] h-[44rem] w-[44rem] -translate-x-1/2 opacity-[0.16]">
          <div className="h-full w-full rounded-full bg-[#D93A2E] blur-[120px] animate-pulse-glow motion-reduce:animate-none" />
        </div>
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(245,243,238,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(245,243,238,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 20%, transparent 100%)",
            maskImage: "radial-gradient(ellipse 70% 60% at 50% 0%, black 20%, transparent 100%)",
          }}
        />
        <div className="absolute left-[7%] top-[30%] hidden h-10 w-10 bg-[#C0392B] opacity-[0.18] animate-float motion-reduce:animate-none md:block" />
        <div className="absolute right-[9%] top-[16%] h-14 w-14 rounded-full bg-[#4A8C3F] opacity-[0.16] animate-drift motion-reduce:animate-none" />
        <svg className="absolute bottom-[14%] right-[20%] hidden opacity-[0.18] animate-float-delayed motion-reduce:animate-none md:block" width="52" height="52" viewBox="0 0 40 40">
          <polygon points="2,2 38,2 20,38" fill="#2E5BA8" />
        </svg>
      </div>

      <header className="relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
        <Link
          to="/"
          className="flex items-center gap-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60"
          aria-label="THE NOT FIT"
        >
          <span className="text-base font-semibold tracking-tight">THE</span>
          <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
          <span className="text-base font-semibold tracking-tight">FIT</span>
        </Link>

        <div role="group" aria-label={t.languageLabel} className="flex rounded-full border border-white/15 p-0.5 text-xs font-semibold">
          {LANGS.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setLang(code)}
              aria-pressed={lang === code}
              className={`rounded-full px-3 py-1.5 uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60 ${
                lang === code ? "bg-[#F5F3EE] text-[#0A0A0F]" : "text-[#F5F3EE]/60 hover:text-[#F5F3EE]"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
      </header>

      <main className="relative z-10 mx-auto w-full max-w-6xl px-5 pb-20 pt-14 sm:px-8 sm:pt-20 lg:pt-28">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#F5F3EE]/55 animate-in fade-in-0 duration-700 fill-mode-both motion-reduce:animate-none">
          {t.eyebrow}
        </p>

        {/* The statement is the hero. Institutions bright, connective words muted; words arrive one by one. */}
        <h1
          key={lang}
          className="mt-8 max-w-4xl text-balance text-[26px] font-semibold leading-[1.22] tracking-tight text-[#F5F3EE]/50 sm:text-4xl sm:leading-[1.18] lg:text-[42px] lg:leading-[1.15]"
        >
          {grant.statement.map((token, i) => (
            <Fragment key={i}>
              <span
                className={`inline-block animate-in fade-in-0 slide-in-from-bottom-2 duration-700 ease-out fill-mode-both motion-reduce:animate-none ${
                  token.strong ? "text-[#F5F3EE]" : ""
                }`}
                style={{ animationDelay: `${200 + i * 38}ms` }}
              >
                {token.text}
              </span>
              {i < grant.statement.length - 1 ? " " : null}
            </Fragment>
          ))}
        </h1>

        <section className="mt-16 sm:mt-24" aria-labelledby="partners-heading">
          <h2 id="partners-heading" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F3EE]/45">
            {t.partnersHeading}
          </h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {GRANT_LOGOS.map((logo, i) => (
              <div
                key={logo.key}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-8 py-10 transition-colors hover:border-[#D93A2E]/50 hover:bg-white/[0.05] animate-in fade-in-0 slide-in-from-bottom-4 fill-mode-both motion-reduce:animate-none"
                style={{ animationDelay: `${900 + i * 150}ms`, animationDuration: "900ms" }}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                  style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(217,58,46,0.18), transparent 70%)" }}
                />
                <div className="relative flex h-44 items-center justify-center sm:h-48">
                  <img
                    src={logo.src}
                    alt={logo.name[lang]}
                    className="max-h-full w-auto max-w-[85%] object-contain opacity-90 transition-opacity duration-500 group-hover:opacity-100"
                  />
                </div>
                <p className="relative mt-6 text-center text-xs uppercase tracking-[0.18em] text-[#F5F3EE]/45">{logo.name[lang]}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 border-t border-white/10 pt-8">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8 lg:grid-cols-4">
            {t.facts.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 90}>
                <dt className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F3EE]/40">{fact.label}</dt>
                <dd className="mt-2">
                  {fact.href ? (
                    <Link
                      to={fact.href}
                      className="rounded text-lg font-semibold text-[#F5F3EE] underline-offset-4 transition-colors hover:text-[#E8574B] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60"
                    >
                      {fact.value}
                    </Link>
                  ) : (
                    <span className="text-lg font-semibold text-[#F5F3EE]">{fact.value}</span>
                  )}
                  <p className="mt-1 text-sm text-[#F5F3EE]/50">{fact.detail}</p>
                </dd>
              </Reveal>
            ))}
          </dl>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10">
        <Reveal>
          <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 text-xs text-[#F5F3EE]/45 sm:flex-row sm:px-8">
            <p>© 2026 THE NOT FIT. {t.rights}</p>
            <nav className="flex flex-wrap items-center justify-center gap-5">
              <Link to="/privacy" className="rounded transition-colors hover:text-[#F5F3EE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60">{t.privacy}</Link>
              <Link to="/terms" className="rounded transition-colors hover:text-[#F5F3EE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60">{t.terms}</Link>
              <Link to="/support" className="rounded transition-colors hover:text-[#F5F3EE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60">{t.support}</Link>
              <Link to="/" className="inline-flex items-center gap-1.5 rounded font-medium text-[#F5F3EE] transition-colors hover:text-[#E8574B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8574B]/60">
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                {t.backHome}
              </Link>
            </nav>
          </div>
        </Reveal>
      </footer>
    </div>
  )
}
